import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { CoreContext } from 'context/CoreContext'
import useI18n from 'hooks/useI18n'
import { languages } from 'utils/options'
import { apiRequest, exposeStrapiError } from 'utils'
import {
  buildPortalDetailRoute,
  getProductAreaFromPath,
  getPortalConfig,
} from 'utils/portal'
import { trackEvent } from 'services/analytics'
import {
  ReadCourseSurvey,
  SubmitCourseSurvey,
} from 'services/surveys'
import { SURVEY_ERROR_CODES } from 'constants/survey'

import {
  buildSurveyAnswers,
  findFirstInvalidSurveyQuestion,
  validateSurveyQuestion,
} from './helpers'

function portalFromPath(pathname) {
  if (pathname.startsWith('/mvu-lite') || pathname.startsWith('/mvu-essentials')) return 'essentials'
  if (pathname.startsWith('/mvu-accelerator')) return 'accelerator'
  return 'universe'
}

export default function useController() {
  const history = useHistory()
  const location = useLocation()
  const { courseId } = useParams()
  const { language } = useContext(CoreContext)
  const { t } = useI18n()
  const locale = useMemo(
    () => languages.find(item => item.id === language)?.ref || 'en',
    [language]
  )
  const portal = useMemo(() => portalFromPath(location.pathname), [location.pathname])
  const portalConfig = useMemo(() => getPortalConfig(portal), [portal])
  const questionFocusRef = useRef(null)
  const viewedRef = useRef(false)

  const [answers, setAnswers] = useState({})
  const [completion, setCompletion] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [errors, setErrors] = useState({})
  const [loadError, setLoadError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [started, setStarted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [survey, setSurvey] = useState(null)

  const loadSurvey = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    const result = await apiRequest(() => ReadCourseSurvey(courseId, locale))
    if (exposeStrapiError(result)) {
      setLoadError(result?.error?.message || t('survey_load_error'))
      setLoading(false)
      return
    }

    setSurvey(result?.data || null)
    if (result?.data?.status?.completed) {
      setCompletion({
        completionMessage: result?.data?.completionMessage,
        submittedAt: result?.data?.status?.submittedAt,
      })
    }
    if (!viewedRef.current && result?.data?.id) {
      viewedRef.current = true
      trackEvent('survey_viewed', {
        content_type: 'survey',
        content_id: result.data.id,
        content_name: result.data.title,
        course_id: result.data.course?.id || courseId,
        course_name: result.data.course?.title,
        question_count: result.data.questions?.length || 0,
        required: !!result.data.required,
        language: locale,
      })
    }
    setLoading(false)
  }, [courseId, locale, t])

  useEffect(() => {
    loadSurvey()
  }, [loadSurvey])

  useEffect(() => {
    if (!started || !questionFocusRef.current) return
    const frame = window.requestAnimationFrame(() => questionFocusRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [currentIndex, started])

  useEffect(() => {
    if (!started || completion || !Object.keys(answers).length) return undefined
    const warnBeforeUnload = event => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [answers, completion, started])

  const currentQuestion = useMemo(
    () => survey?.questions?.[currentIndex] || null,
    [currentIndex, survey?.questions]
  )
  const currentError = currentQuestion ? errors?.[currentQuestion.id] : null
  const questionCount = survey?.questions?.length || 0
  const progressPercentage = questionCount
    ? ((currentIndex + 1) / questionCount) * 100
    : 0
  const isLastQuestion = currentIndex === questionCount - 1
  const estimatedMinutes = Math.max(1, Math.ceil(questionCount * 0.5))

  const handleStart = useCallback(() => {
    setStarted(true)
    trackEvent('survey_started', {
      content_type: 'survey',
      content_id: survey?.id,
      content_name: survey?.title,
      course_id: survey?.course?.id || courseId,
      course_name: survey?.course?.title,
      question_count: questionCount,
      required: !!survey?.required,
      language: locale,
    })
  }, [courseId, locale, questionCount, survey])

  const handleAnswer = useCallback((value) => {
    if (!currentQuestion?.id) return
    setAnswers(previous => ({
      ...previous,
      [currentQuestion.id]: value,
    }))
    setErrors(previous => ({
      ...previous,
      [currentQuestion.id]: null,
    }))
  }, [currentQuestion?.id])

  const validateCurrent = useCallback(() => {
    if (!currentQuestion) return false
    const error = validateSurveyQuestion(
      currentQuestion,
      answers?.[currentQuestion.id],
      t('survey_required_answer')
    )
    if (error) {
      setErrors(previous => ({
        ...previous,
        [currentQuestion.id]: error,
      }))
      trackEvent('survey_validation_failed', {
        content_type: 'survey',
        content_id: survey?.id,
        course_id: survey?.course?.id || courseId,
        question_id: currentQuestion.id,
        question_type: currentQuestion.type,
        language: locale,
      })
      return false
    }
    return true
  }, [answers, courseId, currentQuestion, locale, survey, t])

  const handleContinue = useCallback(() => {
    if (!validateCurrent()) return
    setCurrentIndex(index => Math.min(index + 1, questionCount - 1))
  }, [questionCount, validateCurrent])

  const handleBack = useCallback(() => {
    if (currentIndex === 0) {
      setStarted(false)
      return
    }
    setCurrentIndex(index => Math.max(0, index - 1))
  }, [currentIndex])

  const handleSubmit = useCallback(async () => {
    if (submitting || !survey) return
    const firstInvalid = findFirstInvalidSurveyQuestion(
      survey.questions,
      answers,
      t('survey_required_answer')
    )
    if (firstInvalid >= 0) {
      setCurrentIndex(firstInvalid)
      const question = survey.questions[firstInvalid]
      setErrors(previous => ({
        ...previous,
        [question.id]: validateSurveyQuestion(
          question,
          answers?.[question.id],
          t('survey_required_answer')
        ),
      }))
      trackEvent('survey_validation_failed', {
        content_type: 'survey',
        content_id: survey.id,
        course_id: survey.course?.id || courseId,
        question_id: question.id,
        question_type: question.type,
        language: locale,
      })
      return
    }

    setSubmitting(true)
    const result = await apiRequest(() => SubmitCourseSurvey(
      courseId,
      buildSurveyAnswers(survey.questions, answers),
      locale,
      getProductAreaFromPath(location.pathname)
    ))
    if (result?.error?.details?.code === SURVEY_ERROR_CODES.ALREADY_SUBMITTED) {
      setCompletion({
        completionMessage: survey.completionMessage,
        submittedAt: result?.error?.details?.submittedAt,
      })
      setSubmitting(false)
      return
    }
    if (exposeStrapiError(result)) {
      setSubmitting(false)
      return
    }

    setCompletion(result?.data || {
      completionMessage: survey.completionMessage,
      submittedAt: new Date().toISOString(),
    })
    trackEvent('survey_submitted', {
      content_type: 'survey',
      content_id: survey.id,
      content_name: survey.title,
      course_id: survey.course?.id || courseId,
      course_name: survey.course?.title,
      question_count: questionCount,
      required: !!survey.required,
      language: locale,
    })
    setSubmitting(false)
  }, [answers, courseId, locale, location.pathname, questionCount, submitting, survey, t])

  const handleBackToCourse = useCallback(() => {
    history.push(buildPortalDetailRoute(
      portal,
      'courses',
      survey?.course?.id || courseId
    ))
  }, [courseId, history, portal, survey?.course?.id])

  return {
    answers,
    completion,
    currentError,
    currentIndex,
    currentQuestion,
    estimatedMinutes,
    isLastQuestion,
    loadError,
    loading,
    portalConfig,
    progressPercentage,
    questionCount,
    questionFocusRef,
    started,
    submitting,
    survey,
    t,
    handleAnswer,
    handleBack,
    handleBackToCourse,
    handleContinue,
    handleStart,
    handleSubmit,
  }
}
